/**
 * Mock fixtures for dev.
 *
 * !! DEMO-DATA NOTE !!
 * The "Northstar Health" org (MOCK_ORG) + everything keyed to it — its
 * programs, cohorts, outreach feed, KPIs — is the SHOWCASE demo dataset.
 * When wiring real Django endpoints in Phase B, KEEP this set behind a
 * `?demo=northstar` flag (or a `VITE_PLATFORM_DEMO_DATA=1` env) so we can
 * still drive a live walkthrough with fully-populated data without having
 * to seed a real database. Everything else can be stripped at API cutover.
 */

export interface MockOrg {
  id: number;
  slug: string;
  name: string;
}

export interface MockUser {
  id: number;
  email: string;
  name: string;
  initials: string;
}

/** Shape that lands as the `data` payload of an `agent_agenda` event on seed. */
export interface StarterAgenda {
  thread: string;
  text: string;
  hypothesis?: string;
  nextAction?: string;
}

/** Shape that lands as the `data` payload of a `user_memory` event on seed. */
export interface StarterMemory {
  text: string;
  category?: "program" | "behavior" | "context" | "preference";
}

/** Shape that lands as the `data` payload of a `user_goal` event on seed. */
export interface StarterGoal {
  name: string;
  description?: string;
  target?: string;
  deadlineWeeks?: number;
}

/** Branding tokens passed down to ui_agent for branded artifacts. */
export interface BrandingTokens {
  /** Brand accent color, hex. Used sparingly — never as a flat fill. */
  accent: string;
  /** Headline font family name (Google Fonts). Empty = inherit EON Inter. */
  headlineFont: string;
  /** Logo URL (S3 in prod, any URL in dev). */
  logoUrl: string;
  /** Prefix prepended to push-notification titles, e.g. "Northstar:". */
  pnTitlePrefix: string;
}

export interface MockProgram {
  id: string;
  name: string;
  version: number;
  status: "live" | "draft" | "paused";
  description: string;
  memberCount: number;
  starterAgendaCount: number;
  updatedRelative: string;
  identityOverlay: string;
  /** Seeded as `agent_agenda` events on first cohort redemption. */
  starterAgendas: StarterAgenda[];
  /** Seeded as `user_memory` events on first cohort redemption. */
  starterMemory: StarterMemory[];
  /** Seeded as `user_goal` events on first cohort redemption. */
  starterGoals: StarterGoal[];
  /** Priority domains the agent biases toward when picking what to work on. */
  focusAreas: string[];
  /** Brand visual + push-notification tokens. */
  branding: BrandingTokens;
}

/** Predefined focus areas. Brands can add custom strings on top of these. */
export const FOCUS_AREA_PRESETS = [
  "sleep",
  "recovery",
  "hrv",
  "metabolic",
  "nutrition",
  "weight",
  "strength",
  "training",
  "stress",
  "hydration",
  "energy",
  "mood",
] as const;

export interface MockCohort {
  id: string;
  name: string;
  programId: string;
  programName: string;
  status: "active" | "paused";
  memberCount: number;
  weeklyActive: number;
  redemptionRate: number;
}

export interface MockOutreach {
  id: string;
  channel: "push" | "chat" | "email";
  cohortName: string;
  timestampIso: string;
  body: string;
  subject?: string;
  outcome: string;
  userPseudoId: string;
}

export const MOCK_ORG: MockOrg = {
  id: 1,
  slug: "northstar",
  name: "Northstar Health",
};

export const MOCK_USER: MockUser = {
  id: 42,
  email: "alex@northstarhealth.com",
  name: "Alex Reyes",
  initials: "AR",
};

export const MOCK_KPIS = {
  members: 2341,
  membersDelta: 42,
  weeklyActivePct: 61,
  weeklyActiveDelta: 3,
  msgsPerUserPerWeek: 124.2,
  msgsDelta: 0.4,
  costPerUserPerWeek: 0.18,
  costDelta: -0.02,
};

export const MOCK_AGENT_PULSE = {
  inFlight: 127,
  lastHour: 612,
  todayCounts: {
    artifacts: 84,
    agendas_opened: 31,
    outreach: 1204,
    agendas_dropped: 12,
  },
};

export const MOCK_TOP_TOPICS = [
  { name: "Weight Loss and Nutrition", count: 412 },
  { name: "Recovery & HRV", count: 287 },
  { name: "Caffeine timing", count: 143 },
  { name: "Protein intake", count: 96 },
  { name: "Stress & breathing", count: 71 },
];

export const MOCK_ENGAGEMENT_30D = Array.from({ length: 30 }, (_, i) => ({
  day: i + 1,
  active: Math.round(
    1400 + Math.sin(i * 0.6) * 220 + Math.sin(i * 0.21) * 140 + i * 8,
  ),
}));

/** Daily series for the Cohort engagement tab — DAU vs WAU + org baseline. */
export const MOCK_COHORT_ENGAGEMENT_DAILY = Array.from(
  { length: 30 },
  (_, i) => ({
    day: i + 1,
    dau: Math.round(
      820 + Math.sin(i * 0.7) * 110 + Math.sin(i * 0.32) * 90 + i * 4,
    ),
    wau: Math.round(
      1500 + Math.sin(i * 0.45) * 60 + Math.sin(i * 0.18) * 40 + i * 2.5,
    ),
    orgBaselineDau: Math.round(
      720 + Math.sin(i * 0.55) * 80 + Math.sin(i * 0.21) * 60 + i * 3,
    ),
  }),
);

/** Day-N retention curve — fraction of D0 cohort still active. */
export const MOCK_RETENTION = [
  { day: 0, retained: 1.0 },
  { day: 1, retained: 0.82 },
  { day: 3, retained: 0.71 },
  { day: 7, retained: 0.63 },
  { day: 14, retained: 0.55 },
  { day: 21, retained: 0.51 },
  { day: 30, retained: 0.48 },
];

/** Engagement funnel — fraction of cohort hitting each step in last 7d. */
export const MOCK_ENGAGEMENT_FUNNEL = [
  { step: "Opened app", count: 1655, pct: 0.9 },
  { step: "Tapped a push", count: 1303, pct: 0.71 },
  { step: "Opened an email", count: 957, pct: 0.52 },
  { step: "Viewed an artifact", count: 882, pct: 0.48 },
  { step: "Replied in chat", count: 478, pct: 0.26 },
];

export const MOCK_PROGRAMS: MockProgram[] = [
  {
    id: "prog_metabolic",
    name: "Northstar Metabolic Reset",
    version: 12,
    status: "live",
    description:
      "Coach members through 12 weeks of recovery-led metabolic improvement.",
    memberCount: 1841,
    starterAgendaCount: 4,
    updatedRelative: "3d ago",
    identityOverlay: `You speak to Northstar Health members as the metabolic coach they
came to us for. Reference recovery, HRV, sleep latency, and meal timing
explicitly. Tone: confident, science-led, never preachy. Avoid medical
claims. When suggesting interventions, frame them as experiments with
measurable outcomes the member can see in their own data — your job is
to turn Northstar's protocols into actions they will actually take.`,
    starterAgendas: [
      {
        thread: "metabolic-baseline",
        text: "Establish baseline HRV, RHR, sleep latency in week 1.",
        hypothesis:
          "Members under-report sleep onset by 15-30min. Wearable data will reveal the truth.",
        nextAction:
          "Ask about typical bedtime → wake time on day 2; cross-check against wearable.",
      },
      {
        thread: "meal-timing-window",
        text: "Push the eating window to finish 3h before bed by week 4.",
        hypothesis:
          "Late dinner is the #1 confounder for deep sleep in this cohort.",
        nextAction:
          "Ask for dinner time tonight. Flag any meal after 8pm with sleep impact data.",
      },
      {
        thread: "caffeine-cutoff",
        text: "Find each member's personal caffeine cutoff time.",
        hypothesis:
          "Most users tolerate caffeine until ~2pm; outliers will be obvious in HRV variance.",
        nextAction:
          "Ask when their last coffee was today. Correlate to last 3 nights of HRV.",
      },
      {
        thread: "week-12-handoff",
        text: "Prepare member for program completion + ongoing maintenance.",
        nextAction: "Activate in week 11. Build a personal protocol card.",
      },
    ],
    starterMemory: [
      {
        text: "Member is enrolled in Northstar's 12-week Metabolic Reset program.",
        category: "program",
      },
      {
        text: "Member completed Northstar's baseline health assessment at intake (bloodwork, body composition, fitness test).",
        category: "context",
      },
      {
        text: "Northstar's protocol prioritizes meal timing and sleep before increasing training volume.",
        category: "program",
      },
    ],
    starterGoals: [
      {
        name: "Improve metabolic health",
        description:
          "Move HOMA-IR, fasting glucose, and waist circumference in the right direction over 12 weeks.",
        target: "HOMA-IR < 1.5 by week 12",
        deadlineWeeks: 12,
      },
      {
        name: "Stabilize sleep",
        description:
          "Hit ≥ 7h sleep with < 25min onset latency on 5+ nights per week.",
        target: "5/7 nights",
        deadlineWeeks: 8,
      },
    ],
    focusAreas: ["sleep", "metabolic", "hrv", "nutrition", "recovery"],
    branding: {
      accent: "#56c2ff",
      headlineFont: "",
      logoUrl: "",
      pnTitlePrefix: "Northstar:",
    },
  },
  {
    id: "prog_strength",
    name: "Northstar Strength Block 6",
    version: 3,
    status: "draft",
    description:
      "Six-week heavy compound phase with recovery-aware deloads driven by HRV.",
    memberCount: 500,
    starterAgendaCount: 6,
    updatedRelative: "1d ago",
    identityOverlay: `You speak to athletes in a structured strength block.
Reference training volume, RPE, sleep debt, and recovery score. Tone:
direct, demanding, but never punitive. Push for compliance with the
program first; once that's locked, expand into nutrition and sleep.`,
    starterAgendas: [
      {
        thread: "rpe-calibration",
        text: "Calibrate the member's RPE self-reporting in week 1.",
        hypothesis:
          "Most lifters under-report RPE by ~1 point. Compare reported RPE to bar-speed estimates.",
        nextAction:
          "Ask for top set RPE after each session; flag mismatches with HRV recovery the next day.",
      },
      {
        thread: "deload-triggers",
        text: "Watch for cumulative recovery drift across the 6-week block.",
        hypothesis:
          "3+ consecutive days of recovery < 33% signals an unscheduled deload need.",
        nextAction:
          "Pre-empt the member with an unscheduled deload recommendation before they break.",
      },
    ],
    starterMemory: [
      {
        text: "Member is in a 6-week heavy compound strength block (squat, bench, deadlift, OHP).",
        category: "program",
      },
      {
        text: "Member chose Northstar specifically for structured strength programming.",
        category: "context",
      },
    ],
    starterGoals: [
      {
        name: "Hit block PR on at least one compound",
        description: "Block goal — a 2.5-5kg PR on squat, bench, or deadlift.",
        deadlineWeeks: 6,
      },
    ],
    focusAreas: ["strength", "recovery", "hrv", "training"],
    branding: {
      accent: "#ff6363",
      headlineFont: "",
      logoUrl: "",
      pnTitlePrefix: "",
    },
  },
];

export const MOCK_COHORTS: MockCohort[] = [
  {
    id: "coh_pilot_q2",
    name: "Northstar Pilot Q2",
    programId: "prog_metabolic",
    programName: "Northstar Metabolic Reset v12",
    status: "active",
    memberCount: 1841,
    weeklyActive: 1307,
    redemptionRate: 0.78,
  },
  {
    id: "coh_strength",
    name: "Northstar Strength Block 6",
    programId: "prog_strength",
    programName: "Northstar Strength Block 6 v3",
    status: "paused",
    memberCount: 500,
    weeklyActive: 312,
    redemptionRate: 0.62,
  },
  {
    id: "coh_internal",
    name: "Internal QA",
    programId: "prog_metabolic",
    programName: "Northstar Metabolic Reset v12",
    status: "active",
    memberCount: 18,
    weeklyActive: 16,
    redemptionRate: 1,
  },
];

const now = Date.now();
const minutesAgo = (m: number) => new Date(now - m * 60_000).toISOString();

export const MOCK_OUTREACH: MockOutreach[] = [
  {
    id: "out_1",
    channel: "push",
    cohortName: "Northstar Pilot Q2",
    timestampIso: minutesAgo(4),
    body: "Your deep sleep dipped last night 👀",
    outcome: "tapped 32s later → opened conversation",
    userPseudoId: "user_2841",
  },
  {
    id: "out_2",
    channel: "chat",
    cohortName: "Northstar Pilot Q2",
    timestampIso: minutesAgo(12),
    body: "Built you a recovery tracker. Pinned to your home — it'll update every morning. I'll check in tomorrow to see if the picture looks any different after your easy day.",
    outcome: "viewed artifact 2 min later",
    userPseudoId: "user_2102",
  },
  {
    id: "out_3",
    channel: "email",
    cohortName: "Internal QA",
    timestampIso: minutesAgo(22),
    subject: "One number that explains your last 3 days",
    body: "Quick story about your HRV trend — and the one experiment I want you to run this week…",
    outcome: "opened 4 min ago",
    userPseudoId: "user_3401",
  },
  {
    id: "out_4",
    channel: "push",
    cohortName: "Northstar Pilot Q2",
    timestampIso: minutesAgo(34),
    body: "Quick thing about your morning caffeine — saw something interesting",
    outcome: "delivered",
    userPseudoId: "user_1992",
  },
  {
    id: "out_5",
    channel: "chat",
    cohortName: "Northstar Pilot Q2",
    timestampIso: minutesAgo(58),
    body: "Three nights in a row of >90 min deep sleep. Whatever you're doing on Tue/Wed/Thu — let's lock it in.",
    outcome: "replied 6 min later",
    userPseudoId: "user_2741",
  },
];

// ============================================================================
// Analytics fixtures — cohort health view
// ============================================================================

export interface DomainCoverage {
  domain: string;
  label: string;
  covered: number;
  total: number;
  /** Trend in coverage % over the last 30 days (delta in pp). */
  delta30dPp: number;
}

export const MOCK_DOMAIN_COVERAGE: DomainCoverage[] = [
  { domain: "rhr", label: "Resting HR", covered: 1810, total: 1841, delta30dPp: 1.2 },
  { domain: "sleep", label: "Sleep", covered: 1520, total: 1841, delta30dPp: 4.1 },
  { domain: "activity", label: "Activity", covered: 1400, total: 1841, delta30dPp: 0.6 },
  { domain: "hrv", label: "HRV", covered: 1234, total: 1841, delta30dPp: 7.8 },
  { domain: "stress", label: "Stress / breathing", covered: 612, total: 1841, delta30dPp: 3.2 },
  { domain: "meals", label: "Meals logged", covered: 401, total: 1841, delta30dPp: 12.4 },
  { domain: "weight", label: "Weight", covered: 287, total: 1841, delta30dPp: 2.1 },
  { domain: "supplements", label: "Supplements", covered: 89, total: 1841, delta30dPp: 1.7 },
];

export type InsightKind = "experiment" | "anomaly" | "correlation";

export interface AnalyticsInsight {
  id: string;
  kind: InsightKind;
  title: string;
  hook: string;
  detail: string;
  affected: number;
  confidence?: string; // e.g. "p=0.03"
  domains: string[];
  discoveredRelative: string;
}

export const MOCK_INSIGHTS: AnalyticsInsight[] = [
  {
    id: "ins_exp_dinner",
    kind: "experiment",
    title: "Earlier dinner → +11 min deep sleep",
    hook: "The agent ran a 7-day experiment with 92 members.",
    detail:
      "Members who finished dinner before 7pm for 7 consecutive days saw their deep sleep median rise from 64 → 75 min. Largest gains in members whose prior dinner time was after 9pm.",
    affected: 92,
    confidence: "p = 0.03",
    domains: ["sleep", "meals"],
    discoveredRelative: "2d ago",
  },
  {
    id: "ins_anom_latency",
    kind: "anomaly",
    title: "Cohort-wide sleep latency drift",
    hook: "Sleep onset moved +12 min later over the last 9 days.",
    detail:
      "612 members affected. Strongest correlation with the regional heatwave that started May 6. Hypothesis: bedroom temps > 22°C delay onset. Suggestion: surface cooling-protocol tip in next outreach.",
    affected: 612,
    domains: ["sleep"],
    discoveredRelative: "5h ago",
  },
  {
    id: "ins_corr_caffeine",
    kind: "correlation",
    title: "Caffeine cutoff before 2pm → +24 min deep sleep",
    hook: "612 members fit this profile.",
    detail:
      "Members whose last logged caffeine is before 2pm show a deep-sleep median of 87 min, vs cohort median of 72 min. Effect holds when controlling for age, training load, and time-zone shifts.",
    affected: 612,
    confidence: "r = 0.41",
    domains: ["sleep", "caffeine"],
    discoveredRelative: "1d ago",
  },
];

/** Histogram of average sleep duration across the cohort. */
export const MOCK_SLEEP_DISTRIBUTION = [
  { bin: "< 5h", members: 73 },
  { bin: "5–6h", members: 192 },
  { bin: "6–7h", members: 451 },
  { bin: "7–8h", members: 568 },
  { bin: "8–9h", members: 198 },
  { bin: "> 9h", members: 38 },
];

/** Cohort sleep over program weeks (week 0 = enrollment). */
export const MOCK_SLEEP_TREND = Array.from({ length: 10 }, (_, i) => {
  const base = 6.4 + i * 0.04 + Math.sin(i * 0.7) * 0.05;
  return {
    week: i,
    median: Number((base + 0.2).toFixed(2)),
    p25: Number((base - 0.4).toFixed(2)),
    p75: Number((base + 0.7).toFixed(2)),
  };
});

export interface SleepSubMetric {
  label: string;
  unit: string;
  currentMedian: number;
  deltaSinceWeek0: number;
  /** If lower is better, set `inverted: true`. */
  inverted?: boolean;
}

export const MOCK_SLEEP_SUBMETRICS: SleepSubMetric[] = [
  { label: "Deep sleep", unit: "min", currentMedian: 72, deltaSinceWeek0: 9 },
  { label: "REM", unit: "min", currentMedian: 95, deltaSinceWeek0: 1 },
  { label: "Sleep latency", unit: "min", currentMedian: 18, deltaSinceWeek0: -7, inverted: true },
  { label: "Wake events", unit: "/night", currentMedian: 1.8, deltaSinceWeek0: -0.3, inverted: true },
];

export interface Archetype {
  id: string;
  name: string;
  members: number;
  share: number;
  traits: string[];
  recommendation: string;
}

export const MOCK_ARCHETYPES: Archetype[] = [
  {
    id: "recovery_led",
    name: "Recovery-led",
    members: 681,
    share: 0.37,
    traits: ["High HRV (median 64)", "Consistent sleep", "Lower training strain"],
    recommendation:
      "Push novel stimulus weeks — they have headroom to grow without compromising recovery.",
  },
  {
    id: "consistency_builders",
    name: "Consistency builders",
    members: 405,
    share: 0.22,
    traits: ["Steady habits", "Low variance week over week", "Mid-range biomarkers"],
    recommendation:
      "Celebrate streaks; gentle nudge toward one harder week to break the plateau.",
  },
  {
    id: "high_strain",
    name: "High-strain athletes",
    members: 258,
    share: 0.14,
    traits: ["Strain > 16 daily", "Variable recovery", "Highest training volume in cohort"],
    recommendation:
      "Watch recovery < 33% flags; pre-empt deload before recovery falls 2 days in a row.",
  },
  {
    id: "recovery_deficit",
    name: "Recovery-deficit",
    members: 276,
    share: 0.15,
    traits: ["Sleep < 6h frequently", "Elevated RHR", "Missed agenda actions"],
    recommendation:
      "Lead with sleep work. Defer training-volume asks until 5/7 nights ≥ 7h.",
  },
  {
    id: "emerging_engagement",
    name: "Emerging",
    members: 221,
    share: 0.12,
    traits: ["< 14 days on platform", "Sparse data coverage", "Onboarding agendas active"],
    recommendation:
      "Onboarding-week interventions: baseline assessment + 3 small wins to lock retention.",
  },
];

/**
 * Behavior heatmap: density of a given activity by hour-of-day × day-of-week.
 * Values are normalized 0..1 for color intensity. Shows sleep onset density
 * by default — flip to other domains via a switcher later.
 */
export const MOCK_BEHAVIOR_HEATMAP = (() => {
  // Generate a realistic sleep-onset distribution: peak around 22-24h on
  // weekdays, shift slightly later on Fri/Sat.
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const hours = Array.from({ length: 24 }, (_, h) => h);
  const grid: { day: string; hour: number; intensity: number }[] = [];
  for (const day of days) {
    const shift = day === "Fri" || day === "Sat" ? 1.2 : day === "Sun" ? -0.3 : 0;
    for (const h of hours) {
      const center = 22.5 + shift;
      const dist = Math.abs(h - center);
      const wrap = Math.min(dist, 24 - dist);
      let intensity = Math.max(0, 1 - wrap / 3.5);
      // Tiny noise to keep it interesting.
      intensity = Math.max(0, Math.min(1, intensity + (Math.sin(h + days.indexOf(day) * 0.7) * 0.06)));
      grid.push({ day, hour: h, intensity: Number(intensity.toFixed(2)) });
    }
  }
  return grid;
})();

export interface GoalTrajectory {
  id: string;
  name: string;
  target: string;
  deadlineWeeks: number;
  weekNow: number;
  onTrackNow: number; // fraction 0..1
  onTrackAtWeek0: number;
  projectedAtDeadline: number;
  /** Series over weeks: fraction on track. */
  series: { week: number; onTrack: number }[];
}

export const MOCK_GOAL_TRAJECTORIES: GoalTrajectory[] = [
  {
    id: "g_homa",
    name: "Improve metabolic health",
    target: "HOMA-IR < 1.5",
    deadlineWeeks: 12,
    weekNow: 9,
    onTrackNow: 0.41,
    onTrackAtWeek0: 0.08,
    projectedAtDeadline: 0.62,
    series: [
      { week: 0, onTrack: 0.08 },
      { week: 1, onTrack: 0.11 },
      { week: 2, onTrack: 0.14 },
      { week: 3, onTrack: 0.18 },
      { week: 4, onTrack: 0.23 },
      { week: 5, onTrack: 0.28 },
      { week: 6, onTrack: 0.33 },
      { week: 7, onTrack: 0.36 },
      { week: 8, onTrack: 0.39 },
      { week: 9, onTrack: 0.41 },
    ],
  },
  {
    id: "g_sleep",
    name: "Stabilize sleep",
    target: "5/7 nights ≥ 7h, < 25min onset",
    deadlineWeeks: 8,
    weekNow: 8,
    onTrackNow: 0.46,
    onTrackAtWeek0: 0.14,
    projectedAtDeadline: 0.46,
    series: [
      { week: 0, onTrack: 0.14 },
      { week: 1, onTrack: 0.19 },
      { week: 2, onTrack: 0.25 },
      { week: 3, onTrack: 0.31 },
      { week: 4, onTrack: 0.36 },
      { week: 5, onTrack: 0.40 },
      { week: 6, onTrack: 0.43 },
      { week: 7, onTrack: 0.45 },
      { week: 8, onTrack: 0.46 },
    ],
  },
];

export interface RiskBucket {
  id: string;
  label: string;
  description: string;
  members: number;
  withAgentAgenda: number;
}

export const MOCK_RISK_BUCKETS: RiskBucket[] = [
  {
    id: "sleep_decline",
    label: "Declining sleep",
    description: "< 6h on 7+ consecutive nights",
    members: 47,
    withAgentAgenda: 38,
  },
  {
    id: "hrv_drop",
    label: "Persistent HRV drop",
    description: "10%+ below 30-day baseline for a week",
    members: 31,
    withAgentAgenda: 27,
  },
  {
    id: "disengaged",
    label: "Disengaging",
    description: "No app open or wearable sync in 7+ days",
    members: 64,
    withAgentAgenda: 52,
  },
];

